import { spacing } from '@scality/core-ui';
import { Children, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { revealActionsOnHover } from '../../widget-actions';

export type MetricCardGridProps = {
  /** A sentence above the grid. The caller composes it — the grid reads no values. */
  header?: ReactNode;
  /** Controls at the right of the header row, such as an export. */
  actions?: ReactNode;
  children: ReactNode;
  /** Caps the row so three cards do not stretch across a wide panel. */
  maxWidth?: string;
};

/** The width one card is allowed, as a grid track and on its own. */
const CARD_WIDTH = '14rem';

/** Below this a card stops reading, so the grid wraps rather than shrink a track past it. */
const CARD_MIN_WIDTH = '11rem';

/**
 * `auto-fit` with a stretching track: cards keep a readable width and wrap on the *container's*
 * width, so a grid dropped into a narrow side panel reflows instead of overflowing it. The grid
 * asks for a full complement of cards and no wider, which is what holds a track to a card's width
 * while they all fit, and lets the tracks share the leftover once they no longer do — a dashboard
 * cell is narrower than the complement, and the slack belongs in the cards rather than beside
 * the last column.
 *
 * A lone card is laid out as a flex row that hugs it instead. A one-track grid still reserves the
 * container's full width, which leaves a card stranded against a stretch of empty background —
 * and, in a dashboard cell, sizes the cell to that emptiness rather than to the card.
 *
 * Both branches claim a card's width rather than merely capping one: a dashboard row sizes its
 * cells to their content, and a widget that only ever caps shrinks to the width of its own digits.
 */
const Grid = styled.div<{ $maxWidth: string; $cardCount: number }>`
  gap: ${spacing.f16};
  margin: ${spacing.r16} 0;

  ${({ $cardCount, $maxWidth }) =>
    $cardCount === 1
      ? css`
          display: flex;
          min-width: ${CARD_WIDTH};
          max-width: 100%;

          > * {
            flex: 1;
            min-width: 0;
          }
        `
      : css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(${CARD_MIN_WIDTH}, 100%), 1fr));
          width: min(${$maxWidth}, calc(${$cardCount} * ${CARD_WIDTH} + ${$cardCount - 1} * ${spacing.f16}));
          max-width: 100%;
        `}
`;

/** A `div` without a headline to carry: an empty heading is one a screen reader still announces. */
const Header = styled.h3`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.r8};
  overflow-wrap: anywhere;
  /* The cards size the widget: a long title wraps inside them rather than stretching the cell. */
  width: 0;
  min-width: 100%;
  /* Two lines' worth whatever the title, so a dashboard row keeps its cards on one baseline. */
  min-height: 2.4em;
`;

const Widget = styled.div`
  ${revealActionsOnHover}
`;

/** A row of `MetricCard`s under an optional headline. */
export const MetricCardGrid = ({ header, actions, children, maxWidth = '46rem' }: MetricCardGridProps) => (
  <Widget>
    {(header || actions) && (
      <Header as={header ? 'h3' : 'div'}>
        {header}
        {actions}
      </Header>
    )}
    <Grid $maxWidth={maxWidth} $cardCount={Children.count(children)}>
      {children}
    </Grid>
  </Widget>
);
